import type { BlockText } from "@do/shared-types";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import clsx from "clsx";
import { padding } from "polished";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { MarkdownRenderer, PencilIcon } from "../atoms";

export type ChatMessageTextProps = BlockText & {
  disabled: boolean;
  id: string | undefined;
  idx: number;
  isTestingMode: boolean;
  mergedCtxData?: Record<string, string> | null; // Not used at the moment but will be used in the future to replace params in template strings
  onEditBotResponse?: (messageId: string, openDialog: boolean) => void;
  user: boolean;
};
export const ChatMessageText: FC<ChatMessageTextProps> = ({
  disabled,
  id,
  idx,
  isTestingMode,
  metadata,
  onEditBotResponse,
  text,
  user,
}) => {
  const theme = useTheme();

  const isResponseEditionEnabled = useMemo(
    () =>
      isTestingMode &&
      typeof metadata === "object" &&
      metadata?.[0]?.["generative_response"] &&
      onEditBotResponse,
    [isTestingMode, metadata, onEditBotResponse]
  );

  const removeHighlight = useCallback(() => {
    const elemsHighlighted = Array.from(document.getElementsByClassName("highlight"));
    elemsHighlighted.forEach((elem) => {
      elem.classList.remove("highlight");
    });
  }, []);

  const highLightBlock = useCallback(
    (blockId: string, e: MouseEvent) => {
      if (!isResponseEditionEnabled || !onEditBotResponse) return;
      e.stopPropagation();
      removeHighlight();
      const elemToHighLight = document.getElementById(blockId);
      if (elemToHighLight) {
        elemToHighLight.classList.add("highlight");
      }
      onEditBotResponse(id || "", false);
    },
    [id, isResponseEditionEnabled, onEditBotResponse, removeHighlight]
  );

  const onClickOutside = useCallback(
    (e: MouseEvent) => {
      if (!isResponseEditionEnabled || !onEditBotResponse) return;
      e.stopPropagation();
      // disable highlight if click happens outside a highlighted zone
      const targetClassName = (e?.target as Element)?.className;
      if (!targetClassName?.includes?.("highlight")) removeHighlight();
      onEditBotResponse("", false);
    },
    [isResponseEditionEnabled, onEditBotResponse, removeHighlight]
  );

  useEffect(() => {
    if (!isTestingMode) return;
    const style = document.createElement("style");
    style.innerHTML = `
        .highlight {
            box-shadow: inset 0px 0px 0px 3px ${theme.palette.secondary.main};
        }`;
    document.head.appendChild(style);
    document.addEventListener("click", onClickOutside);
    return () => {
      document.removeEventListener("click", onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestingMode, theme.palette.secondary.main, user]);

  return (
    <Box
      borderRadius={15}
      display="flex"
      flexDirection="row"
      justifyContent={user ? "flex-end" : "flex-start"}
    >
      <Typography
        className={clsx({ chatMessageRobot: !user, chatMessageHuman: user })}
        component="div"
        id={`block-${id}-${idx}`}
        onClick={(e) =>
          isResponseEditionEnabled && !user
            ? highLightBlock(`block-${id}-${idx}`, e as unknown as MouseEvent)
            : null
        }
        maxWidth="calc(100% - 50px)"
        textOverflow="initial"
        variant="body1"
        sx={{
          ...padding("6px", "15px"),
          color: user ? theme.palette.primary.contrastText : theme.palette.text.primary,
          bgcolor: user ? theme.palette.primary.main : theme.palette.background.paper,
          borderRadius: user ? "15px 15px 0px" : "0px 15px 15px",
          boxShadow: "2px 2px 8px rgba(105, 120, 140, 0.15)",
          lineHeight: 1.4,
          wordWrap: "break-word",
        }}
      >
        {user ? (
          <span>{text}</span>
        ) : (
          <Box>
            <MarkdownRenderer allowFullScreenImg text={text} />
            {isResponseEditionEnabled && onEditBotResponse ? (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <IconButton
                  onClick={() => id && onEditBotResponse(id, true)}
                  disabled={disabled}
                  sx={{
                    padding: 0,
                    opacity: disabled ? 0.4 : 1,
                    transition: "opacity 0.5s ease-in-out",
                  }}
                >
                  <PencilIcon />
                </IconButton>
              </Box>
            ) : null}
          </Box>
        )}
      </Typography>
    </Box>
  );
};
